/*
 * Email parsing helpers shared between ingestion (Node/CJS) and UI (browser).
 * No DOM-only or Node-only APIs in this module. Uses module.exports so both
 * sides can consume it: `require()` from api/* and webpack-interop `import`
 * from src/*.
 */

// ───────────────────────────────────────────────────────────────
// HTML entity decoder (named + numeric + hex). No DOM needed.
// ───────────────────────────────────────────────────────────────
const NAMED_ENTITIES = {
  amp: "&", lt: "<", gt: ">", quot: '"', apos: "'",
  nbsp: " ", copy: "\u00A9", reg: "\u00AE", trade: "\u2122",
  hellip: "\u2026", mdash: "\u2014", ndash: "\u2013",
  lsquo: "\u2018", rsquo: "\u2019", ldquo: "\u201C", rdquo: "\u201D",
};

function decodeHtmlEntities(str) {
  if (!str) return "";
  return String(str)
    .replace(/&#x([0-9a-fA-F]+);/g, (_, hex) => {
      const n = parseInt(hex, 16);
      try { return Number.isFinite(n) ? String.fromCodePoint(n) : _; } catch (_e) { return _; }
    })
    .replace(/&#(\d+);/g, (_, dec) => {
      const n = parseInt(dec, 10);
      try { return Number.isFinite(n) ? String.fromCodePoint(n) : _; } catch (_e) { return _; }
    })
    .replace(/&([a-zA-Z][a-zA-Z0-9]*);/g, (full, name) => {
      const v = NAMED_ENTITIES[name.toLowerCase()];
      return v !== undefined ? v : full;
    });
}

// ───────────────────────────────────────────────────────────────
// Strip HTML tags + decode entities + collapse whitespace.
// ───────────────────────────────────────────────────────────────
function stripHtmlTags(html) {
  if (!html) return "";
  let s = String(html);
  s = s.replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, "")
       .replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, "");
  s = s.replace(/<\/(p|div|li|h[1-6]|tr|blockquote)\s*>/gi, "\n")
       .replace(/<br\s*\/?>/gi, "\n");
  s = s.replace(/<[^>]*>/g, "");
  s = decodeHtmlEntities(s);
  s = s.replace(/[ \t\u00A0]+/g, " ")
       .replace(/\n[ \t]+/g, "\n")
       .replace(/\n{3,}/g, "\n\n");
  return s.trim();
}

// ───────────────────────────────────────────────────────────────
// Remove quoted-reply history. Truncate at the earliest match across
// several common patterns.
// ───────────────────────────────────────────────────────────────
function stripQuotedReply(bodyText) {
  if (!bodyText) return bodyText || "";
  const s = String(bodyText);
  let earliest = -1;

  const setEarlier = (idx) => {
    if (idx >= 0 && (earliest < 0 || idx < earliest)) earliest = idx;
  };

  const onWrote = s.match(/^On .{1,200}\s(wrote|schreef|escribió):$/m);
  if (onWrote && onWrote.index !== undefined) setEarlier(onWrote.index);

  const origMsg = s.match(/^-----\s?Original Message\s?-----$/mi);
  if (origMsg && origMsg.index !== undefined) setEarlier(origMsg.index);

  const underscore = s.match(/^_{10,}$/m);
  if (underscore && underscore.index !== undefined) setEarlier(underscore.index);

  const outlookHeader = s.match(/\n\s*\n\s*From:\s.*\n/);
  if (outlookHeader && outlookHeader.index !== undefined) setEarlier(outlookHeader.index);

  const lines = s.split(/\r?\n/);
  let running = 0;
  let blockStartCharIdx = -1;
  let charCursor = 0;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (line.startsWith("> ") || line === ">") {
      if (running === 0) blockStartCharIdx = charCursor;
      running++;
      if (running >= 3) {
        setEarlier(blockStartCharIdx);
        break;
      }
    } else {
      running = 0;
      blockStartCharIdx = -1;
    }
    charCursor += line.length + 1;
  }

  if (earliest < 0) return s.trimEnd();
  return s.slice(0, earliest).trimEnd();
}

// ───────────────────────────────────────────────────────────────
// Strip signature conservatively. Only high-confidence matches.
// ───────────────────────────────────────────────────────────────
function stripSignature(bodyText) {
  if (!bodyText) return bodyText || "";
  const s = String(bodyText);
  const rfc = s.match(/(^|\n)-- ?(\n|$)/);
  if (rfc && rfc.index !== undefined) {
    return s.slice(0, rfc.index).trimEnd();
  }
  const mobile = s.match(/\n\s*Sent from my (iPhone|iPad|Android|mobile|BlackBerry|Samsung[^\n]*)/i);
  if (mobile && mobile.index !== undefined) {
    return s.slice(0, mobile.index).trimEnd();
  }
  const getApp = s.match(/\n\s*Get Outlook for (iOS|Android)/i);
  if (getApp && getApp.index !== undefined) {
    return s.slice(0, getApp.index).trimEnd();
  }
  return s;
}

// ───────────────────────────────────────────────────────────────
// Short preview for list views.
// ───────────────────────────────────────────────────────────────
function makePreview(bodyText, maxLen) {
  if (!bodyText) return "";
  const limit = maxLen || 140;
  let s = stripQuotedReply(String(bodyText));
  s = stripSignature(s);
  s = s.replace(/\s+/g, " ").trim();
  if (s.length > limit) s = s.slice(0, limit).trimEnd() + "…";
  return s;
}

// ───────────────────────────────────────────────────────────────
// Minimal RFC 5545 iCalendar parser. Returns null on failure.
// Handles line folding (continuation lines begin with space/tab).
// ───────────────────────────────────────────────────────────────
function parseICalendar(icsText) {
  if (!icsText) return null;
  try {
    const text = String(icsText);

    const eventMatch = text.match(/BEGIN:VEVENT([\s\S]*?)END:VEVENT/);
    if (!eventMatch) return null;
    const eventBlock = eventMatch[1];

    // Unfold continuation lines (RFC 5545 §3.1).
    const rawLines = eventBlock.split(/\r?\n/);
    const unfolded = [];
    for (const line of rawLines) {
      if ((line.startsWith(" ") || line.startsWith("\t")) && unfolded.length > 0) {
        unfolded[unfolded.length - 1] += line.slice(1);
      } else {
        unfolded.push(line);
      }
    }

    const props = {};
    for (const line of unfolded) {
      const colonIdx = line.indexOf(":");
      if (colonIdx === -1) continue;
      const keyPart = line.slice(0, colonIdx);
      const value = line.slice(colonIdx + 1);
      const semiIdx = keyPart.indexOf(";");
      const propName = (semiIdx === -1 ? keyPart : keyPart.slice(0, semiIdx)).toUpperCase().trim();
      if (propName) props[propName] = value;
    }

    const methodMatch = text.match(/^METHOD:([^\r\n]+)/mi);
    const method = methodMatch ? methodMatch[1].trim().toUpperCase() : null;

    // Parse iCal date / date-time. Floating times are treated as UTC
    // (TZID resolution is out of scope for this minimal parser).
    const parseIcsDate = (dtVal) => {
      if (!dtVal) return null;
      const v = String(dtVal).trim();
      if (/^\d{8}$/.test(v)) {
        const y = v.slice(0, 4), mo = v.slice(4, 6), d = v.slice(6, 8);
        const dt = new Date(`${y}-${mo}-${d}T00:00:00Z`);
        return isNaN(dt.getTime()) ? null : dt.toISOString();
      }
      const m = v.match(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})(Z?)$/);
      if (m) {
        const y = m[1], mo = m[2], d = m[3], h = m[4], mi = m[5], s = m[6];
        const dt = new Date(`${y}-${mo}-${d}T${h}:${mi}:${s}Z`);
        return isNaN(dt.getTime()) ? null : dt.toISOString();
      }
      return null;
    };

    const unescapeIcs = (v) => String(v || "")
      .replace(/\\n/gi, "\n")
      .replace(/\\,/g, ",")
      .replace(/\\;/g, ";")
      .replace(/\\\\/g, "\\");

    const title = unescapeIcs(props.SUMMARY || "").trim();
    const start = parseIcsDate(props.DTSTART);
    const end = parseIcsDate(props.DTEND);
    const location = unescapeIcs(props.LOCATION || "").trim() || null;

    let organizer = null;
    if (props.ORGANIZER) {
      const mailto = String(props.ORGANIZER).match(/mailto:([^\s>;,]+)/i);
      if (mailto) organizer = mailto[1].toLowerCase();
    }

    let joinUrl = null;
    if (props.URL) {
      joinUrl = String(props.URL).trim();
    } else if (props.DESCRIPTION) {
      const desc = unescapeIcs(props.DESCRIPTION);
      const meetMatch = desc.match(/https?:\/\/(?:meet\.google\.com|[a-z0-9.-]*zoom\.us|teams\.microsoft\.com|[a-z0-9.-]*webex\.com)\/[^\s<>"]+/i);
      if (meetMatch) joinUrl = meetMatch[0];
    }

    let status = null;
    if (method === "CANCEL") status = "cancelled";
    else if (props.STATUS) {
      const st = String(props.STATUS).toLowerCase().trim();
      if (st === "confirmed" || st === "tentative" || st === "cancelled") status = st;
    }

    if (!title || !start) return null;
    return { title, start, end, location, organizer, joinUrl, status };
  } catch (err) {
    try { console.warn("[emailParsing] parseICalendar failed:", err.message); } catch (_e) {}
    return null;
  }
}

// ───────────────────────────────────────────────────────────────
// Heuristic calendar-invite detection. Secondary to finding a
// text/calendar MIME part at ingestion.
// ───────────────────────────────────────────────────────────────
function detectCalendarInvite(email) {
  if (!email) return false;
  const subject = String(email.subject || "");
  if (/^(Invitation|Accepted|Declined|Tentative|Updated invitation|Canceled event|Re: Invitation):\s/i.test(subject)) {
    return true;
  }
  const bodyText = String(email.body_text || "");
  const bodyHtml = String(email.body_html || "");
  if (bodyText.includes("BEGIN:VCALENDAR") || bodyHtml.includes("BEGIN:VCALENDAR")) return true;
  return false;
}

module.exports = {
  decodeHtmlEntities,
  stripHtmlTags,
  stripQuotedReply,
  stripSignature,
  makePreview,
  parseICalendar,
  detectCalendarInvite,
};
